import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import {
  FaBookOpen,
  FaCheck,
  FaClipboardList,
  FaGraduationCap,
  FaSave,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const TAG_ALT = "ALTERNATIVE";

//Created By Cursor — absent mark codes accepted in mark entry
const ABSENT_MARK = "AB";

//Created By Cursor
function isAbsentMarkCode(value) {
  return String(value || "").trim().toUpperCase() === ABSENT_MARK;
}

//Created By Cursor
function isPartialAbsentMarkCode(value) {
  const v = String(value || "").trim().toUpperCase();
  return v === "A" || v === "AB";
}

//Created By Cursor
function markInputBlankOrSpaceError(value) {
  const s = String(value ?? "");
  if (s.trim() === "") {
    return "Mark cannot be blank";
  }
  if (/\s/.test(s)) {
    return "Spaces are not allowed";
  }
  return "";
}

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (item.section_id === "No" || !item.section_name) {
    return item.class_name || "Class";
  }
  return `${item.class_name}: ${item.section_name}`;
}

//Created By Cursor
function handleMarkKeyDown(e, studentIndex, paperIndex, papersCount) {
  if (e.key === "Tab" && !e.shiftKey && paperIndex === papersCount - 1) {
    e.preventDefault();
    const saveButton = document.querySelector(
      `button[data-student-index="${studentIndex}"]`
    );
    if (saveButton) saveButton.focus();
    return;
  }

  const move = (delta) => {
    const table = e.target.closest("table");
    if (!table) return;
    const inputs = Array.from(table.querySelectorAll('input[type="text"]'));
    const currentIndex = inputs.indexOf(e.target);
    const next = inputs[currentIndex + delta];
    if (next) {
      e.preventDefault();
      next.focus();
      next.select();
    }
  };

  if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) move(1);
  if (e.key === "Tab" && e.shiftKey) move(-1);
  if (e.key === "ArrowRight" || e.key === "ArrowDown") move(1);
  if (e.key === "ArrowLeft" || e.key === "ArrowUp") move(-1);
}

const ExamAddMarkSubjectWise = () => {
  const history = useHistory();
  const {
    exam_id: EXAM_ID,
    session_id: SESSION_ID,
    exam_name,
    exam_status,
  } = useParams();

  const EXAM_NAME = decodeURIComponent(exam_name || "");
  const EXAM_STATUS = decodeURIComponent(exam_status || "");
  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [classList, setClassList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [subjectId, setSubjectId] = useState(null);
  const [class_id, setClassId] = useState(null);
  const [section_id, setSectionId] = useState(null);
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [selectedSubjectPosition, setSelectedSubjectPosition] = useState(0);
  const [mMarkStatus, setMarkStatus] = useState([]);
  const [mStudentAltSub, setStudentAltSub] = useState([]);
  const [mStudentList, setStudentList] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [classSubjectPaper, setClassSubjectPaper] = useState([]);

  const selectedClass = useMemo(() => {
    return selectedClassPosition >= 0 && classList[selectedClassPosition]
      ? classList[selectedClassPosition]
      : null;
  }, [selectedClassPosition, classList]);

  const classSectionData = useMemo(() => {
    if (!selectedClass) return { classId: null, sectionId: null };
    return {
      classId: selectedClass.class_id,
      sectionId:
        selectedClass.section_id === "null" ? "No" : selectedClass.section_id,
    };
  }, [selectedClass]);

  const selectedSubject = useMemo(() => {
    return subjectList?.length > 0 && subjectList[selectedSubjectPosition]
      ? subjectList[selectedSubjectPosition]
      : null;
  }, [subjectList, selectedSubjectPosition]);

  const isAlternativeSubject = useMemo(() => {
    if (!subjectId || !classSubjectPaper.length) return false;
    const subjectIdLower = subjectId.toLowerCase();
    return classSubjectPaper.some(
      (paper) =>
        paper.subject_id?.toLowerCase() === subjectIdLower &&
        paper.subject_type?.toLowerCase() === TAG_ALT.toLowerCase()
    );
  }, [subjectId, classSubjectPaper]);

  const studentAltSubMap = useMemo(() => {
    const map = new Map();
    if (!subjectId) return map;
    const subjectIdLower = subjectId.toLowerCase();
    mStudentAltSub.forEach((item) => {
      if (item.subject_id?.toLowerCase() === subjectIdLower) {
        const studentIdLower = item.student_id?.toLowerCase();
        if (studentIdLower) map.set(studentIdLower, true);
      }
    });
    return map;
  }, [mStudentAltSub, subjectId]);

  const markStatusMap = useMemo(() => {
    const map = new Map();
    mMarkStatus.forEach((status) => {
      const studentIdLower = status.student_id?.toLowerCase();
      if (studentIdLower) {
        if (!map.has(studentIdLower)) map.set(studentIdLower, []);
        map.get(studentIdLower).push(status);
      }
    });
    return map;
  }, [mMarkStatus]);

  const dirtyCount = useMemo(
    () =>
      filteredStudents.reduce(
        (sum, student) =>
          sum +
          (student.mark_status?.filter((p) => p.changed_mark === "1").length ||
            0),
        0
      ),
    [filteredStudents]
  );

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setClassList([]);
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      try {
        const response = await axios.get(
          `${API_BASE()}getClassWithSubjectsPartAndPaper.php`,
          {
            params: {
              school_code: SessionManager.getSchoolCode(),
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          setClassList(response.data.data || []);
          setSelectedClassPosition(-1);
          setSubjectList([]);
          setFilteredStudents([]);
        } else {
          setClassList([]);
          myToast.notify_warning(response.data?.api_remark);
        }
      } catch (error) {
        console.error("Error fetching class data:", error);
        myToast.notify_error("Error fetching class data");
        setClassList([]);
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID]
  );

  //Created By Cursor
  const getClassList = useCallback(
    (selected_position) => {
      if (
        classList.length > 0 &&
        selected_position >= 0 &&
        classList[selected_position]
      ) {
        setSubjectList(classList[selected_position].subject || []);
        setSelectedSubjectPosition(0);
      }
    },
    [classList]
  );

  //Created By Cursor
  const getSubjectPaper = useCallback(
    (selectedSubjectId) => {
      if (!selectedClass || !selectedSubjectId) {
        setClassSubjectPaper([]);
        return;
      }
      const subjectIdLower = selectedSubjectId.toLowerCase();
      setClassSubjectPaper(
        (selectedClass.subject_paper || []).filter(
          (paper) => paper.subject_id?.toLowerCase() === subjectIdLower
        )
      );
    },
    [selectedClass]
  );

  //Created By Cursor
  const getClassStudentMarks = useCallback(
    (CLASS_ID, SECTION_ID, SUBJECT_ID) => {
      if (!CLASS_ID || !SECTION_ID || !SUBJECT_ID) return;

      setLoadingMarks(true);
      axios
        .get(`${API_BASE()}getClassSubjectMarks.php`, {
          params: {
            school_code: SessionManager.getSchoolCode(),
            exam_id: EXAM_ID,
            class_id: CLASS_ID,
            section_id: SECTION_ID,
            subject_id: SUBJECT_ID,
            session_id: SESSION_ID,
          },
        })
        .then((res) => res.data)
        .then((response) => {
          setLoadingMarks(false);
          if (response.api_status === "OK") {
            setMarkStatus(response.data.mark_status || []);
            setStudentAltSub(response.data.student_alt_sub || []);
            setStudentList(response.data.student_list || []);
          } else {
            myToast.notify_warning(response.api_remark);
            setMarkStatus([]);
            setStudentAltSub([]);
            setStudentList([]);
          }
        })
        .catch((error) => {
          setLoadingMarks(false);
          myToast.notify_error("Error fetching student marks");
          console.error("Error fetching student marks:", error);
        });
    },
    [EXAM_ID, SESSION_ID]
  );

  //Created By Cursor
  const isSubGrantedToStudent = useCallback(
    (studentId) => {
      if (!studentId) return false;
      return studentAltSubMap.has(studentId.toLowerCase());
    },
    [studentAltSubMap]
  );

  //Created By Cursor
  const getMark = useCallback(
    (studentId) => {
      if (!studentId || !classSubjectPaper.length) return null;

      const studentIdLower = studentId.toLowerCase();
      const mMark = markStatusMap.get(studentIdLower);

      if (!mMark || mMark.length === 0) {
        return classSubjectPaper.map((paper) => ({
          ...paper,
          obtained_mark: "",
          grade_obtained: "",
          obtained_mark_tmp: "",
          grade_obtained_tmp: "",
          obtained_mark_new: "",
          grade_obtained_new: "",
          changed_mark: "0",
          set_error: "0",
          paper_type: paper.paper_type || "Normal",
        }));
      }

      const markMap = new Map();
      mMark.forEach((mark) => {
        const paperId = mark.exam_paper_id?.toLowerCase();
        if (paperId) markMap.set(paperId, mark);
      });

      return classSubjectPaper
        .filter((paper) => paper.exam_paper_id?.toLowerCase())
        .map((paper) => {
          const existingMark = markMap.get(paper.exam_paper_id.toLowerCase());
          if (existingMark) {
            return {
              ...paper,
              ...existingMark,
              paper_type: paper.paper_type || existingMark.paper_type || "Normal",
              exam_paper_id: paper.exam_paper_id,
              exam_paper_name:
                paper.exam_paper_name || existingMark.exam_paper_name,
              max_mark: paper.max_mark || existingMark.max_mark,
              max_grade: paper.max_grade || existingMark.max_grade,
              is_mark_system:
                paper.is_mark_system || existingMark.is_mark_system,
              is_grade_system:
                paper.is_grade_system || existingMark.is_grade_system,
              subject_type: paper.subject_type || existingMark.subject_type,
              obtained_mark: existingMark.obtained_mark || "",
              grade_obtained: existingMark.grade_obtained || "",
              obtained_mark_tmp: existingMark.obtained_mark_tmp,
              grade_obtained_tmp: existingMark.grade_obtained_tmp,
              obtained_mark_new: existingMark.obtained_mark_new,
              grade_obtained_new: existingMark.grade_obtained_new,
              changed_mark: existingMark.changed_mark || "0",
              set_error: existingMark.set_error || "0",
              errorMessage: existingMark.errorMessage,
            };
          }
          return {
            ...paper,
            obtained_mark: "",
            grade_obtained: "",
            obtained_mark_tmp: "",
            grade_obtained_tmp: "",
            obtained_mark_new: "",
            grade_obtained_new: "",
            changed_mark: "0",
            set_error: "0",
            paper_type: paper.paper_type || "Normal",
          };
        });
    },
    [markStatusMap, classSubjectPaper]
  );

  useEffect(() => {
    if (!mStudentList || mStudentList.length === 0) {
      setFilteredStudents([]);
      return;
    }

    const filteredList = mStudentList
      .filter((student) => {
        if (isAlternativeSubject) {
          return isSubGrantedToStudent(student.student_enrollment_no);
        }
        return true;
      })
      .map((student) => {
        const mark = getMark(student.student_enrollment_no);
        const studentData = { ...student, mark_status: mark || [] };

        if (mark && mark.length > 0) {
          studentData.mark_status = mark.map((markItem) => {
            if (markItem.is_mark_system === "1") {
              return {
                ...markItem,
                obtained_mark_tmp:
                  markItem.obtained_mark_tmp !== undefined &&
                  markItem.obtained_mark_tmp !== null
                    ? markItem.obtained_mark_tmp
                    : markItem.obtained_mark || "",
                obtained_mark_new:
                  markItem.obtained_mark_new !== undefined
                    ? markItem.obtained_mark_new
                    : "",
                changed_mark:
                  markItem.changed_mark !== undefined
                    ? markItem.changed_mark
                    : "",
                set_error:
                  markItem.set_error !== undefined ? markItem.set_error : "",
              };
            }
            return {
              ...markItem,
              grade_obtained_tmp:
                markItem.grade_obtained_tmp !== undefined &&
                markItem.grade_obtained_tmp !== null
                  ? markItem.grade_obtained_tmp
                  : markItem.grade_obtained || "",
              grade_obtained_new:
                markItem.grade_obtained_new !== undefined
                  ? markItem.grade_obtained_new
                  : "",
              changed_mark:
                markItem.changed_mark !== undefined
                  ? markItem.changed_mark
                  : "",
            };
          });
        }

        return studentData;
      });

    setFilteredStudents(filteredList);
  }, [mStudentList, isAlternativeSubject, isSubGrantedToStudent, getMark]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  useEffect(() => {
    if (selectedClassPosition >= 0) {
      getClassList(selectedClassPosition);
    }
  }, [selectedClassPosition, getClassList]);

  useEffect(() => {
    if (!selectedClass || !subjectList || subjectList.length === 0) return;

    const { classId, sectionId } = classSectionData;
    setClassId(classId);
    setSectionId(sectionId);

    if (selectedSubject) {
      setSubjectId(selectedSubject.subject_id);
      getSubjectPaper(selectedSubject.subject_id);
      getClassStudentMarks(classId, sectionId, selectedSubject.subject_id);
    }
  }, [
    selectedSubjectPosition,
    subjectList,
    selectedClass,
    selectedSubject,
    classSectionData,
    getSubjectPaper,
    getClassStudentMarks,
  ]);

  //Created By Cursor
  const prepareMarkStatusForAPI = useCallback((markStatus) => {
    if (!Array.isArray(markStatus)) return [];

    return markStatus.map((paper) => {
      const preparedPaper = { ...paper };

      if (preparedPaper.is_mark_system === "1") {
        preparedPaper.obtained_mark =
          preparedPaper.obtained_mark !== null &&
          preparedPaper.obtained_mark !== undefined
            ? String(preparedPaper.obtained_mark)
            : "";

        if (preparedPaper.changed_mark === "1") {
          preparedPaper.obtained_mark_new =
            preparedPaper.obtained_mark_new !== null &&
            preparedPaper.obtained_mark_new !== undefined
              ? String(preparedPaper.obtained_mark_new)
              : "";
        } else {
          preparedPaper.obtained_mark_new = preparedPaper.obtained_mark;
        }
      } else {
        preparedPaper.grade_obtained =
          preparedPaper.grade_obtained !== null &&
          preparedPaper.grade_obtained !== undefined
            ? String(preparedPaper.grade_obtained)
            : "";

        if (preparedPaper.changed_mark === "1") {
          preparedPaper.grade_obtained_new =
            preparedPaper.grade_obtained_new !== null &&
            preparedPaper.grade_obtained_new !== undefined
              ? String(preparedPaper.grade_obtained_new)
              : "";
        } else {
          preparedPaper.grade_obtained_new = preparedPaper.grade_obtained;
        }
      }

      preparedPaper.changed_mark = preparedPaper.changed_mark || "0";
      return preparedPaper;
    });
  }, []);

  //Created By Cursor
  const saveStudentMark = useCallback(
    (studentIndex) => {
      if (!class_id || !section_id || !subjectId) {
        myToast.notify_warning("Please select class and subject");
        return;
      }

      const student = filteredStudents[studentIndex];
      if (!student) return;

      const hasErrors = student.mark_status?.some(
        (paper) => paper.changed_mark === "1" && paper.set_error === "1"
      );
      if (hasErrors) {
        myToast.notify_warning(
          "Fix invalid marks before saving (number or AB, no blank)"
        );
        return;
      }

      const hasBlankChangedMark = student.mark_status?.some(
        (paper) =>
          paper.changed_mark === "1" &&
          paper.is_mark_system === "1" &&
          markInputBlankOrSpaceError(
            paper.obtained_mark_new ?? paper.obtained_mark_tmp ?? ""
          )
      );
      if (hasBlankChangedMark) {
        myToast.notify_warning("Mark cannot be blank or contain spaces");
        return;
      }

      const hasChanges = student.mark_status?.some(
        (paper) => paper.changed_mark === "1"
      );
      if (!hasChanges) {
        myToast.notify_warning("No changes to save");
        return;
      }

      setSavingStudentId(student.student_enrollment_no);
      const preparedMarkStatus = prepareMarkStatusForAPI(student.mark_status);

      const params = new URLSearchParams({
        exam_id: EXAM_ID,
        class_id,
        section_id,
        subject_id: subjectId,
        session_id: SESSION_ID,
        student_enrollment_no: student.student_enrollment_no,
        mark_status: JSON.stringify(preparedMarkStatus),
        school_code: SessionManager.getSchoolCode(),
      });

      axios
        .post(`${API_BASE()}AddSingleStudentMarkSubjectWise.php`, params)
        .then((res) => res.data)
        .then((response) => {
          setSavingStudentId(null);
          if (response.api_status === "OK") {
            myToast.notify_success(`Marks saved for ${student.stu_name}`);
            setMarkStatus((prevMarkStatus) => {
              const studentIdLower =
                student.student_enrollment_no.toLowerCase();
              const statusMap = new Map();
              prevMarkStatus.forEach((item) => {
                const key = `${item.student_id?.toLowerCase() || ""}|${
                  item.exam_paper_id?.toLowerCase() || ""
                }`;
                statusMap.set(key, item);
              });

              preparedMarkStatus.forEach((savedPaper) => {
                const paperIdLower = savedPaper.exam_paper_id?.toLowerCase();
                if (!paperIdLower) return;
                const key = `${studentIdLower}|${paperIdLower}`;
                const existingPaper = statusMap.get(key);
                const isMarkSystem = savedPaper.is_mark_system === "1";
                const newMark =
                  savedPaper.obtained_mark_new || savedPaper.obtained_mark || "";
                const newGrade =
                  savedPaper.grade_obtained_new ||
                  savedPaper.grade_obtained ||
                  "";

                if (existingPaper) {
                  statusMap.set(key, {
                    ...existingPaper,
                    ...(isMarkSystem
                      ? {
                          obtained_mark: newMark,
                          obtained_mark_tmp: newMark,
                        }
                      : {
                          grade_obtained: newGrade,
                          grade_obtained_tmp: newGrade,
                        }),
                    obtained_mark_new: "",
                    grade_obtained_new: "",
                    changed_mark: "0",
                    set_error: "0",
                  });
                } else {
                  statusMap.set(key, {
                    ...savedPaper,
                    student_id: student.student_enrollment_no,
                    obtained_mark: isMarkSystem ? newMark : "",
                    grade_obtained: !isMarkSystem ? newGrade : "",
                    obtained_mark_tmp: isMarkSystem ? newMark : "",
                    grade_obtained_tmp: !isMarkSystem ? newGrade : "",
                    obtained_mark_new: "",
                    grade_obtained_new: "",
                    changed_mark: "0",
                    set_error: "0",
                  });
                }
              });

              return Array.from(statusMap.values());
            });
          } else {
            myToast.notify_warning(response.api_remark || "Error saving marks");
          }
        })
        .catch((error) => {
          setSavingStudentId(null);
          myToast.notify_error("Error saving marks");
          console.error("Error saving marks:", error);
        });
    },
    [
      filteredStudents,
      class_id,
      section_id,
      subjectId,
      EXAM_ID,
      SESSION_ID,
      prepareMarkStatusForAPI,
    ]
  );

  //Created By Cursor
  const submitMark = useCallback(() => {
    if (!class_id || !section_id || !subjectId) {
      myToast.notify_warning("Please select class and subject");
      return;
    }

    const hasErrors = filteredStudents.some((student) =>
      student.mark_status?.some(
        (paper) => paper.changed_mark === "1" && paper.set_error === "1"
      )
    );
    if (hasErrors) {
      myToast.notify_warning(
        "Fix invalid marks before saving (number or AB, no blank)"
      );
      return;
    }

    const hasBlankChangedMark = filteredStudents.some((student) =>
      student.mark_status?.some(
        (paper) =>
          paper.changed_mark === "1" &&
          paper.is_mark_system === "1" &&
          markInputBlankOrSpaceError(
            paper.obtained_mark_new ?? paper.obtained_mark_tmp ?? ""
          )
      )
    );
    if (hasBlankChangedMark) {
      myToast.notify_warning("Mark cannot be blank or contain spaces");
      return;
    }

    const hasChanges = filteredStudents.some((student) =>
      student.mark_status?.some((paper) => paper.changed_mark === "1")
    );
    if (!hasChanges) {
      myToast.notify_warning("No changes to submit");
      return;
    }

    setLoading(true);
    const mark_details = filteredStudents.map((item) => ({
      student_enrollment_no: item.student_enrollment_no,
      //Created By Cursor — send array (not double-stringified JSON) for faster API parse
      mark_status: prepareMarkStatusForAPI(item.mark_status),
    }));

    const params = new URLSearchParams({
      exam_id: EXAM_ID,
      class_id,
      section_id,
      subject_id: subjectId,
      session_id: SESSION_ID,
      mark_details: JSON.stringify(mark_details),
      school_code: SessionManager.getSchoolCode(),
    });

    axios
      .post(`${API_BASE()}AddStudentMarkSubjectWiseNew.php`, params)
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          myToast.notify_success("Marks submitted successfully");
          getClassStudentMarks(class_id, section_id, subjectId);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        myToast.notify_error("Error submitting marks");
        console.error("Error submitting marks:", error);
      });
  }, [
    filteredStudents,
    class_id,
    section_id,
    subjectId,
    EXAM_ID,
    SESSION_ID,
    getClassStudentMarks,
    prepareMarkStatusForAPI,
  ]);

  //Created By Cursor
  const handleInputChange = useCallback((studentIndex, paperIndex, mark) => {
    setFilteredStudents((prevStudents) =>
      prevStudents.map((student, i) => {
        if (i !== studentIndex) return student;

        const updatedMarkStatus = [...(student.mark_status || [])];
        if (
          !Array.isArray(updatedMarkStatus) ||
          !updatedMarkStatus[paperIndex]
        ) {
          return student;
        }

        const updatedPaper = { ...updatedMarkStatus[paperIndex] };

        if (updatedPaper.is_mark_system === "1") {
          const max_mark = parseFloat(updatedPaper.max_mark);
          const maxMarkNum = Number.isNaN(max_mark) ? Infinity : max_mark;
          const raw = String(mark ?? "");
          const blankOrSpaceError = markInputBlankOrSpaceError(raw);

          if (blankOrSpaceError) {
            updatedPaper.obtained_mark_tmp = raw;
            updatedPaper.set_error = "1";
            updatedPaper.errorMessage = blankOrSpaceError;
            updatedPaper.changed_mark = "1";
          } else if (isAbsentMarkCode(raw)) {
            //Created By Cursor — store absent as AB (accepts AB / Ab / ab)
            updatedPaper.obtained_mark_new = ABSENT_MARK;
            updatedPaper.obtained_mark_tmp = ABSENT_MARK;
            updatedPaper.changed_mark = "1";
            updatedPaper.set_error = "0";
            delete updatedPaper.errorMessage;
          } else if (isPartialAbsentMarkCode(raw)) {
            //Created By Cursor — allow typing A… toward AB without error yet
            updatedPaper.obtained_mark_tmp = raw;
            updatedPaper.set_error = "0";
            delete updatedPaper.errorMessage;
          } else {
            const numericPattern = /^-?\d*\.?\d*$/;
            if (!numericPattern.test(raw)) {
              updatedPaper.set_error = "1";
              updatedPaper.errorMessage =
                "Enter a number or AB (no blank or spaces)";
              updatedPaper.obtained_mark_tmp = raw;
              updatedMarkStatus[paperIndex] = updatedPaper;
              return { ...student, mark_status: updatedMarkStatus };
            }

            const inputMark = parseFloat(raw);
            if (Number.isNaN(inputMark) || raw.trim() === "") {
              updatedPaper.set_error = "1";
              updatedPaper.errorMessage =
                "Enter a number or AB (no blank or spaces)";
              updatedPaper.obtained_mark_tmp = raw;
            } else if (inputMark < 0) {
              myToast.notify_warning("Marks cannot be negative");
              updatedPaper.set_error = "1";
              updatedPaper.errorMessage = "Marks cannot be negative";
              updatedPaper.obtained_mark_tmp = raw;
            } else if (inputMark > maxMarkNum) {
              myToast.notify_warning(
                `Obtained mark cannot be greater than max mark (${max_mark})`
              );
              updatedPaper.set_error = "1";
              updatedPaper.errorMessage = `Cannot exceed max mark (${max_mark})`;
              updatedPaper.obtained_mark_tmp = raw;
            } else {
              updatedPaper.obtained_mark_new = raw;
              updatedPaper.obtained_mark_tmp = raw;
              updatedPaper.changed_mark = "1";
              updatedPaper.set_error = "0";
              delete updatedPaper.errorMessage;
            }
          }
        } else {
          updatedPaper.grade_obtained_new = mark;
          updatedPaper.grade_obtained_tmp = mark;
          updatedPaper.changed_mark = "1";
        }

        updatedMarkStatus[paperIndex] = updatedPaper;
        return { ...student, mark_status: updatedMarkStatus };
      })
    );
  }, []);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  const detailsPath = `/AdminMain/ExamDetails/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(
    EXAM_NAME || ""
  )}/${encodeURIComponent(EXAM_STATUS || "")}`;

  return (
    <div
      className="content payroll-page sm-page eamsw-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Add Mark Subject Wise
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Mark Entry · Subject Wise</h1>
              <p className="sm-hero__sub">
                {EXAM_NAME || "Exam"} · Enter marks by subject & paper
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {EXAM_STATUS ? (
              <span className="eamsw-status-pill">{EXAM_STATUS}</span>
            ) : null}
            <button
              type="button"
              onClick={() => getExamParticipatingClass({ soft: true })}
              disabled={refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to={detailsPath} className="eamsw-hero-link">
              Exam Details
            </Link>
            <Link to="/AdminMain/ExamList" className="eamsw-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="eamsw-content">
          <div className="eamsw-kpi-row">
            <span className="eamsw-kpi">
              <FaGraduationCap aria-hidden />
              {classList.length} classes
            </span>
            <span className="eamsw-kpi">
              <FaBookOpen aria-hidden />
              {subjectList.length} subjects
            </span>
            <span className="eamsw-kpi">
              <FaUsers aria-hidden />
              {filteredStudents.length} students
            </span>
            <span className="eamsw-kpi">
              <FaClipboardList aria-hidden />
              {dirtyCount} dirty
            </span>
          </div>

          {/* Classes */}
          <section className="eamsw-section-card" aria-label="Select class">
            <div className="eamsw-section-card__header">
              <h2 className="eamsw-section-card__title">Select Class</h2>
              <span className="eamsw-section-card__count">
                {classList.length}
              </span>
            </div>
            <div className="eamsw-section-card__body">
              {classList.length === 0 ? (
                <div className="eamsw-empty">
                  <p className="eamsw-empty__title">No participating classes</p>
                </div>
              ) : (
                <div className="eamsw-chip-grid">
                  {classList.map((item, index) => (
                    <button
                      key={`${item.class_id}-${item.section_id}-${index}`}
                      type="button"
                      className={`eamsw-chip${
                        selectedClassPosition === index ? " is-active" : ""
                      }`}
                      onClick={() => setSelectedClassPosition(index)}
                    >
                      {classLabel(item)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Subjects */}
          {subjectList.length > 0 ? (
            <section className="eamsw-section-card" aria-label="Select subject">
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  Select Subject · {classLabel(selectedClass)}
                </h2>
                <span className="eamsw-section-card__count">
                  {subjectList.length}
                </span>
              </div>
              <div className="eamsw-section-card__body">
                <div className="eamsw-chip-grid">
                  {subjectList.map((item2, index2) => (
                    <button
                      key={item2.subject_id || index2}
                      type="button"
                      className={`eamsw-chip eamsw-chip--subject${
                        selectedSubjectPosition === index2 ? " is-active" : ""
                      }`}
                      onClick={() => setSelectedSubjectPosition(index2)}
                    >
                      {item2.subject_name}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {/* Marks table */}
          {filteredStudents.length > 0 ? (
            <section
              className="eamsw-section-card"
              aria-label="Student marks entry"
            >
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  Marks Entry · {selectedSubject?.subject_name || "Subject"}
                </h2>
                <span className="eamsw-section-card__count">
                  {filteredStudents.length}
                </span>
              </div>
              <div className="eamsw-section-card__body">
                {loadingMarks ? (
                  <div className="eamsw-loading-inline">
                    <MyLoader />
                  </div>
                ) : null}

                {classSubjectPaper.length > 0 ? (
                  <div
                    className={`eamsw-table-wrap${
                      loadingMarks ? " is-busy" : ""
                    }`}
                  >
                    <table className="eamsw-table">
                      <thead>
                        <tr>
                          <th className="eamsw-sticky-left eamsw-col-roll">
                            Roll No
                          </th>
                          <th className="eamsw-sticky-left2 eamsw-col-name">
                            Student Name
                          </th>
                          {classSubjectPaper.map((paper, paperIdx) => {
                            const isAlternative =
                              paper.subject_type?.toLowerCase() ===
                              TAG_ALT.toLowerCase();
                            return (
                              <th
                                key={paper.exam_paper_id || paperIdx}
                                className="eamsw-col-paper"
                              >
                                <div className="eamsw-paper-head">
                                  <span className="eamsw-paper-head__name">
                                    {paper.exam_paper_name}
                                  </span>
                                  <span className="eamsw-paper-head__max">
                                    Max:{" "}
                                    {paper.is_mark_system === "1"
                                      ? paper.max_mark
                                      : paper.max_grade}
                                  </span>
                                  <span
                                    className={`eamsw-paper-badge${
                                      isAlternative ? " is-alt" : ""
                                    }`}
                                  >
                                    {paper.paper_type || "Normal"}
                                  </span>
                                </div>
                              </th>
                            );
                          })}
                          <th className="eamsw-sticky-right eamsw-col-action">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((item, index) => {
                          const mMarkDetails = Array.isArray(item.mark_status)
                            ? item.mark_status
                            : [];
                          const isSaving =
                            savingStudentId === item.student_enrollment_no;

                          return (
                            <tr
                              key={item.student_enrollment_no || index}
                              className={index % 2 === 0 ? "is-even" : ""}
                            >
                              <td className="eamsw-sticky-left eamsw-col-roll">
                                <span className="eamsw-roll">
                                  {item.student_roll_no}
                                </span>
                              </td>
                              <td className="eamsw-sticky-left2 eamsw-col-name">
                                <span
                                  className="eamsw-student-name"
                                  title={item.stu_name}
                                >
                                  {item.stu_name}
                                </span>
                              </td>
                              {mMarkDetails.map((item2, index2) => {
                                const hasSavedMark =
                                  item2.is_mark_system === "1"
                                    ? item2.obtained_mark &&
                                      item2.obtained_mark !== "" &&
                                      item2.changed_mark !== "1"
                                    : item2.grade_obtained &&
                                      item2.grade_obtained !== "" &&
                                      item2.changed_mark !== "1";
                                const inputValue =
                                  item2.is_mark_system === "1"
                                    ? item2.obtained_mark_tmp !== undefined
                                      ? item2.obtained_mark_tmp
                                      : item2.obtained_mark || ""
                                    : item2.grade_obtained_tmp !== undefined
                                      ? item2.grade_obtained_tmp
                                      : item2.grade_obtained || "";

                                return (
                                  <td
                                    key={item2.exam_paper_id || index2}
                                    className="eamsw-col-paper"
                                  >
                                    <div className="eamsw-input-wrap">
                                      <input
                                        type="text"
                                        inputMode="text"
                                        className={`eamsw-input${
                                          item2.set_error === "1"
                                            ? " is-error"
                                            : hasSavedMark
                                              ? " is-saved"
                                              : ""
                                        }${
                                          item2.changed_mark === "1" &&
                                          item2.set_error !== "1"
                                            ? " is-dirty"
                                            : ""
                                        }`}
                                        value={inputValue}
                                        onChange={(e) =>
                                          handleInputChange(
                                            index,
                                            index2,
                                            e.target.value
                                          )
                                        }
                                        onKeyDown={(e) =>
                                          handleMarkKeyDown(
                                            e,
                                            index,
                                            index2,
                                            mMarkDetails.length
                                          )
                                        }
                                        placeholder={
                                          item2.is_mark_system === "1"
                                            ? "Mark / AB"
                                            : "Grade"
                                        }
                                      />
                                      {item2.set_error === "1" ? (
                                        <span className="eamsw-error">
                                          {item2.errorMessage}
                                        </span>
                                      ) : null}
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="eamsw-sticky-right eamsw-col-action">
                                <button
                                  type="button"
                                  data-student-index={index}
                                  className="eamsw-save-btn"
                                  onClick={() => saveStudentMark(index)}
                                  disabled={isSaving || loadingMarks}
                                  onKeyDown={(e) => {
                                    if (
                                      (e.key === "Enter" || e.key === " ") &&
                                      !isSaving &&
                                      !loadingMarks
                                    ) {
                                      e.preventDefault();
                                      saveStudentMark(index);
                                    }
                                  }}
                                >
                                  <FaSave aria-hidden />
                                  {isSaving ? "Saving…" : "Save"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="eamsw-empty">
                    <p className="eamsw-empty__title">
                      No papers found for this subject
                    </p>
                  </div>
                )}
              </div>
            </section>
          ) : selectedClass && subjectId ? (
            <section className="eamsw-section-card">
              <div className="eamsw-section-card__body">
                <div className="eamsw-empty">
                  <p className="eamsw-empty__title">No students found</p>
                  <p className="eamsw-empty__hint">
                    No students for the selected class and subject
                    {isAlternativeSubject
                      ? " (alternative subject filter applied)"
                      : ""}
                    .
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {filteredStudents.length > 0 ? (
            <div className="eamsw-submit-bar">
              <button
                type="button"
                className="eamsw-submit-btn"
                onClick={submitMark}
                disabled={loading || loadingMarks}
              >
                <FaCheck aria-hidden />
                {loading || loadingMarks
                  ? "Submitting…"
                  : `Submit Marks (${filteredStudents.length} students)`}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ExamAddMarkSubjectWise;
